// AsideBar.js
// Purpose: Renders the sidebar navigation for the application, providing access to workspaces, notifications, and user actions.
// Logic: Uses React hooks, context, and state to manage sidebar visibility, navigation, modals, and user interactions.

import { useEffect, useState, useRef, useContext } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Button, Drawer, Tooltip } from "flowbite-react";
import useCommanApi from "../hooks/common-apis";
import useCommonFunctions from "../hooks/common-functions";
import { Nav } from "rsuite";
import { Accordion } from "flowbite-react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { io } from "socket.io-client";
import { socketService } from "./socketService";
import moment from "moment";
import UserContext from "../context/UserContext";
import Avatar from "./Avatar";
import {
  clog,
  getDecryptedUserMenus,
  getEncriptedLoginData,
  getPlanDetails,
  hasMenuAccess,
  setLocalStorageWithEvent,
  isUserOwnerOfWorkspace,
} from "../lib/utils";
import UpgradeModal from "./UpgradeModal";
import MenuWithUpgradeStamp from "./MenuWithUpgradeStamp";
import PopupModals from "./PopupModals";
import KwinOnboardingModal from "./KwinOnboardingModal";

function AsideBar() {
  const {
    setknowLedgeBaseListGlobal,
    workspaceUpdateTrigger,
    setWorkspaceUpdateTrigger,
  } = useContext(UserContext);
  const navigate = useNavigate();
  // offcanvas
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = () => {
    setnotifications((prev) =>
      prev.map((notification) => {
        return { ...notification, status: 1 };
      })
    );
    setIsOpen(false);
  };
  // offcanvas
  const [openModal, setOpenModal] = useState();
  const modalRef = useRef(null);
  const path = useLocation();
  const [modelopen, setModelopen] = useState(false);
  const [selectedWorkspace, setselectedWorkspace] = useState({});
  const { logoutApi, getKnowledgeBaseList, getWorkspaces } = useCommanApi();
  const { getLetters, getBackgroundColor, checkWorkspaceEnabled } =
    useCommonFunctions();
  const [showLogout, setShowLogout] = useState(false);
  const [notifications, setnotifications] = useState([]);
  const [notificationsUnread, setnotificationsUnread] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isKwinOnboardingModalOpen, setIsKwinOnboardingModalOpen] = useState(false);
  const [kwinOnboardingStatus, setKwinOnboardingStatus] = useState([false, false, false, false, false, false]);
  const [showModal, setshowModal] = useState({
    first: false,
    second: false,
    third: false,
  });
  const [popupMessage, setpopupMessage] = useState("");

  const loginData = JSON.parse(localStorage.getItem("login_data") || "{}");
  // Credits state for dynamic updates via socket
  const [creditsData, setCreditsData] = useState({
    bal_credits: loginData?.resp_user?.bal_credits || 0,
    credit_limit: loginData?.resp_user?.credit_limit || 100,
    plan_id: loginData?.resp_user?.plan_id || null,
    subscription_id: loginData?.resp_user?.subscription_id || null,
    user_menus: getDecryptedUserMenus() || {},
    message: "",
  });
  // Force re-render trigger for menu updates
  const [menuUpdateTrigger, setMenuUpdateTrigger] = useState(0);
  // State to track image load errors for fallback to letters
  const [imageErrors, setImageErrors] = useState({});
  const [workspaceList, setworkspaceList] = useState([]);

  let pathname = path.pathname;
  let pathnamesArray = [
    "/account-settings",
    "/member",
    "/manage-space",
    "/billing",
    "/my-settings",
    "/my-workspace",
    "/feedback",
  ];
  const asideBarType = () => {
    if (pathnamesArray.includes(pathname)) {
      return true;
    } else {
      return false;
    }
  };
  clog(asideBarType(), "asideBarType");

  const handleButtonClick = () => {
    setWorkspaceUpdateTrigger((prev) => prev + 1);
    setShowLogout((prev) => !prev); // Toggle the logout button visibility
  };

  const handleLogout = () => {
    // Handle logout logic here
    clog("User logged out");

    setselectedWorkspace({});
    setShowLogout(false);
    logoutApi();
  };

  const getCreatedAt = (date) => {
    if (!date) return "";
    //If date is todays date then return moment from now else return date
    if (moment(date)?.format("YYYY-MM-DD") == moment()?.format("YYYY-MM-DD")) {
      return moment(date)?.fromNow();
    } else {
      return moment(date)?.format("MMM DD");
    }
  };

  const moreLink = (env, id) => {
    if (!env.target.matches(".active")) {
      document.querySelectorAll(".nav-toggle").forEach((e) => {
        e.classList.remove("active");
      });
      document.querySelectorAll(".navmore-link").forEach((e) => {
        e.classList.remove("active");
      });
      env.target.classList.add("active");
      document.getElementById(id).classList.add("active");
    } else {
      env.target.classList.remove("active");
      document.getElementById(id).classList.remove("active");
    }
  };
  const menuToggle = () => {
    document.body.classList.toggle("shrink-menu");
  };
  const getNotifications = async (from) => {
    setNotificationsLoading(true);
    let login_data = JSON.parse(localStorage.getItem("login_data") || "{}");
    if (!login_data || Object.keys(login_data).length == 0) {
      setNotificationsLoading(false);
      return;
    }
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/notifications`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (response.data.status) {
        setnotifications(response?.data?.notifications || []);
      } else {
        clog("Error in fetching notifications");
        setnotifications([]);
      }
    } catch (error) {
      clog("Error in fetching notifications", error);
      setnotifications([]);
      if (error?.response?.status == 401 || error?.response?.status == 403) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        handleLogout();
        navigate("/");
      }
    } finally {
      setNotificationsLoading(false);
    }
  };
  clog(notificationsUnread, "notificationsUnread");
  clog(notifications, "notifications");
  const getNotificationsUnreadUpdate = async () => {
    let login_data = JSON.parse(localStorage.getItem("login_data") || "{}");
    if (!login_data || Object.keys(login_data).length == 0) {
      return;
    }
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/notification/update`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (response.data.status) {
        // setnotifications((prev) =>
        //   prev.map((notification) => {
        //     return { ...notification, status: 1 };
        //   })
        // );
      } else {
        clog("Error in fetching notifications");
        // setnotificationsUnread([]);
      }
    } catch (error) {
      clog("Error in fetching notifications", error);
      // setnotificationsUnread([]);
      if (error?.response?.status == 401 || error?.response?.status == 403) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        handleLogout();
        navigate("/");
      }
    }
  };

  // Freez side bar opne dropdown

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setModelopen(false);
      }
    }
    if (modelopen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modelopen]);
  let login_data = JSON.parse(localStorage.getItem("login_data") || "{}");
  const getWorkspace = async () => {
    let login_data = JSON.parse(localStorage.getItem("login_data") || "{}");
    if (!login_data || Object.keys(login_data).length == 0) {
      return;
    }

    // First, check if there's already a selected_workspace in localStorage
    let stored_selected_workspace = JSON.parse(
      localStorage.getItem("selected_workspace") || "{}"
    );

    let workspace_id;

    // If there's a stored selected workspace, use its workspace_id
    if (
      stored_selected_workspace &&
      Object.keys(stored_selected_workspace).length > 0
    ) {
      workspace_id = stored_selected_workspace?.workspace_id;
      clog("Using stored workspace:", workspace_id);
    } else {
      // Otherwise, fall back to the default workspace from login_data
      workspace_id = login_data?.resp_user?.workspace_id || "";
      clog("Using default workspace from login_data:", workspace_id);
    }

    let workspaces = login_data?.workspacess || [];
    clog(workspaces, "workspaces");
    let selectedWorkspace = workspaces.find(
      (workspace) => workspace?.workspace_id == workspace_id
    );
    if (selectedWorkspace) {
      localStorage.setItem(
        "selected_workspace",
        JSON.stringify(selectedWorkspace)
      );
      setselectedWorkspace(selectedWorkspace);
    } else {
      localStorage.setItem("selected_workspace", JSON.stringify({}));
      setselectedWorkspace({});
    }
  };

  // Get list of all workspaces for the user
  const getWorkspaceList = async () => {
    let response = await getWorkspaces();
    if (response) {
      setworkspaceList(response);
    } else {
      setworkspaceList([]);
    }
  };

  // Function to switch workspace and persist the selection
  const switchWorkspace = (workspace) => {
    // Update selected_workspace in localStorage
    localStorage.setItem("selected_workspace", JSON.stringify(workspace));
    setselectedWorkspace(workspace);

    // Also update the workspace_id, user_menus, and plan_id in login_data to persist across refresh
    let login_data = JSON.parse(localStorage.getItem("login_data") || "{}");
    if (login_data && login_data.resp_user) {
      login_data.resp_user.workspace_id = workspace?.workspace_id;

      // Update plan_id from workspace owner's plan
      // if (workspace?.user?.plan_id) {
      //   login_data.resp_user.plan_id = workspace.user.plan_id;
      // }

      // Update user_menus with encryption if available in workspace
      if (workspace?.user_menus) {
        // Create a copy with the new user_menus (unencrypted)
        const updatedLoginData = {
          ...login_data,
          user_menus: workspace.user_menus,
        };

        // Encrypt and save to localStorage
        const encryptedData = getEncriptedLoginData(updatedLoginData);
        localStorage.setItem("login_data", JSON.stringify(encryptedData));
      } else {
        // If no user_menus in workspace, just save without updating menus
        localStorage.setItem("login_data", JSON.stringify(login_data));
      }
    }

    // Close the popup
    setShowLogout(false);

    // Trigger workspace update to notify all components
    setWorkspaceUpdateTrigger((prev) => prev + 1);

    // Trigger menu update to refresh sidebar
    setMenuUpdateTrigger((prev) => prev + 1);

    // Reload knowledge base list for new workspace if needed
    if (!pathname.includes("knowledge")) {
      getKnowLedgeBaseListFn();
    }

    // hard refresh the page to load data for new workspace and if user is on unauthorized page then redirect to script page
    if (path.pathname == "/unauthorized") {
      window.location.href = "/script";
    } else {
      window.location.reload();
    }
  };

  // const getDatesWiseNotifications = () => {
  //   // Read notifications
  //   let dates = notifications?.map((notification) => {
  //     return notification?.createdAt;
  //   });
  //   let uniqueDates = [...new Set(dates)];
  //   let dateWiseNotifications = uniqueDates.map((date) => {
  //     return {
  //       createdAt: date,
  //       notifications: notifications.filter(
  //         (notification) => notification?.createdAt == date
  //       ),
  //     };
  //   });
  //   clog(dateWiseNotifications, "dateWiseNotifications");
  //   return dateWiseNotifications || [];
  // };
  const getDatesWiseNotifications = () => {
    if (!notifications) return [];

    // Group notifications by month-year
    const monthYearGroups = notifications.reduce((groups, notification) => {
      const date = new Date(notification.createdAt);
      const monthYear = date.toLocaleString("en-US", {
        month: "short",
        year: "numeric",
      });

      if (!groups[monthYear]) {
        groups[monthYear] = [];
      }
      groups[monthYear].push(notification);
      return groups;
    }, {});

    // Convert to array and sort by date (newest first)
    const sortedGroups = Object.entries(monthYearGroups)
      .map(([monthYear, notifications]) => ({
        monthYear,
        notifications,
      }))
      .sort((a, b) => {
        const dateA = new Date(a.notifications[0].createdAt);
        const dateB = new Date(b.notifications[0].createdAt);
        return dateB - dateA;
      });
    clog(sortedGroups, "sortedGroups");
    return sortedGroups;
  };
  const getKnowLedgeBaseListFn = async () => {
    try {
      let login_data = JSON.parse(localStorage.getItem("login_data") || "{}");
      let workspace_id = login_data?.resp_user?.workspace_id;
      clog(workspace_id, "workspace_id");
      if (!workspace_id) {
        return;
      }
      let response = await getKnowledgeBaseList(workspace_id);
      if (response) {
        setknowLedgeBaseListGlobal(response?.data || []);
      } else {
        setknowLedgeBaseListGlobal([]);
      }
    } catch (error) {
      console.error("Error fetching knowledge base list:", error);
      setknowLedgeBaseListGlobal([]);
    } finally {
    }
  };
  useEffect(() => {
    if (selectedWorkspace && Object.keys(selectedWorkspace).length > 0) {
      return;
    }
    getWorkspace();
    getWorkspaceList();
    getNotifications();
    if (!pathname.includes("knowledge")) {
      getKnowLedgeBaseListFn();
    }
  }, [login_data, workspaceUpdateTrigger]);

  const getOnboardingStatus = async () => {
    let login_token = localStorage.getItem("login_token");
    if (!login_token) return;
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/profile/status`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: login_token,
        },
      });
      if (!response.data.error) {
        let status = response.data.profilestatus;
        if (status) {
          setKwinOnboardingStatus([
            !!status.add_website,
            !!status.verify_script,
            !!status.page_tracking,
            !!status.buyer_persona,
            !!status.eamil_account, // Matches typo in API response
            !!status.sales_tools
          ]);
        } else {
          setKwinOnboardingStatus([false, false, false, false, false, false]);
        }
      }
    } catch (error) {
      console.log("Error fetching onboarding status", error);
    }
  };

  useEffect(() => {
    getOnboardingStatus();
  }, [path.pathname,isKwinOnboardingModalOpen]);


  // const socketRef = useRef(null); // Use useRef to store socket

  // useEffect(() => {
  //   const loginData = localStorage.getItem("login_data");

  //   // Exit early if login_data doesn't exist
  //   if (!loginData) return;

  //   const parsedData = JSON.parse(loginData);
  //   const userId = parsedData?.resp_user?.id; // Extract userId if available

  //   if (!userId) return;

  //   // Initialize socket only if it doesn't exist
  //   if (!socketRef.current) {
  //     const SOCKET_URL = process.env.REACT_APP_BASE_SIGNIN_URL; // Replace with your backend URL
  //     socketRef.current = io(SOCKET_URL); // Replace with your server URL
  //   }

  //   socketRef.current.on("connect", () => {
  //     clog("Socket connected");
  //     socketRef.current.emit("join", userId);
  //   });

  //   socketRef.current.on("knowledge_notification", (message) => {
  //     clog("Received notification:", message.knowledge_notification);
  //   });

  //   socketRef.current.on("error", (err) => {
  //     clog("Error: ", err);
  //   });

  //   // Cleanup
  //   return () => {
  //     if (socketRef.current) {
  //       socketRef.current.disconnect(); // Disconnect socket
  //       socketRef.current = null; // Reset socket to prevent reinitialization
  //     }
  //   };
  // }, []);

  useEffect(() => {
    if (!socketService.isConnected()) {
      socketService.initialize();
    }
    // Handle notifications
    // notify("Welcome to the Knowledge Base");
    const handleNotification = (data) => {
      //example data
      // {
      //   contents:'Job Success for Manual agent process ',
      //   knowledge_id:knowledge_id,
      //   content_id: content_id.toString()
      //}
      clog("AsideBar notification:", data);
      let toastMessage = data?.contents || "";
      let status = data?.status;
      if (status == 2) {
        notify(toastMessage, "success");
      } else {
        notify(toastMessage, "error");
      }
      getNotifications();
    };

    function handleSubscriptionNotification(data) {
      clog("Subscription notification:", data);
      // Update credits data with socket data
      setCreditsData({
        bal_credits: data.bal_credits || 0,
        credit_limit: data.credit_limit || 100,
        plan_id: data.user.plan_id,
        subscription_id: data.user.subscription_id,
        user_menus: data.user_menus || {},
        message: data.message || "",
      });

      // Show toast notification if there's a message
      if (data.message) {
        notify(data.message, "warning");
      }
      localStorage.removeItem("stripe_vision_session_id");
      let loginData = JSON.parse(localStorage.getItem("login_data"));
      loginData = {
        ...loginData,
        resp_user: {
          ...loginData.resp_user,
          plan_id: data.user.plan_id,
          subscription_id: data.user.subscription_id,
          bal_credits: data.bal_credits,
          credit_limit: data.credit_limit || 100,
        },
        user_menus: data.user_menus,
      };
      // Use custom setter to trigger localStorage change events
      setLocalStorageWithEvent(
        "login_data",
        JSON.stringify(getEncriptedLoginData(loginData))
      );
      // Trigger menu re-render
      setMenuUpdateTrigger((prev) => prev + 1);
      getNotifications();
    }

    function handleCreditNotification(data) {
      clog("Credit notification:", data);
      // Update credits data with socket data
      setCreditsData({
        bal_credits: data.bal_credits || 0,
        credit_limit: data.credit_limit || 100,
        plan_id: data.plan_id,
        subscription_id: data.subscription_id,
        user_menus: data.user_menus || {},
        message: data.message || "",
      });

      // Show toast notification if there's a message
      if (data.message) {
        notify(data.message, "warning");
      }

      //also update login_data in local storage
      let loginData = JSON.parse(localStorage.getItem("login_data"));
      loginData = {
        ...loginData,
        resp_user: {
          ...loginData.resp_user,
          plan_id: data.plan_id,
          subscription_id: data.subscription_id,
          bal_credits: data.bal_credits,
          credit_limit: data.credit_limit || 100,
        },
        user_menus: data.user_menus,
      };
      // Use custom setter to trigger localStorage change events
      setLocalStorageWithEvent(
        "login_data",
        JSON.stringify(getEncriptedLoginData(loginData))
      );
      // Trigger menu re-render
      setMenuUpdateTrigger((prev) => prev + 1);
      getNotifications();
    }

    socketService.on("keyword_notification", handleNotification);
    socketService.on("knowledge_notification", handleNotification);
    socketService.on("buyerpercona_notification", handleNotification);
    socketService.on("monthlycalendar_notification", handleNotification);
    socketService.on(
      "upgrade_subscription_notification",
      handleSubscriptionNotification
    );
    socketService.on("user_credit_notification", handleCreditNotification);
    socketService.on("user_updated_notification", handleCreditNotification);

    return () => {
      socketService.off("keyword_notification", handleNotification);
      socketService.off("knowledge_notification", handleNotification);
      socketService.off("buyerpercona_notification", handleNotification);
      socketService.off("monthlycalendar_notification", handleNotification);
      socketService.off(
        "upgrade_subscription_notification",
        // handleNotification
        // () => {}
        handleSubscriptionNotification
      );
      socketService.off("user_credit_notification", handleCreditNotification);
      socketService.off("user_updated_notification", handleCreditNotification);
    };
  }, [path?.pathname, menuUpdateTrigger]);
  const getLinkedInAccessToken = async (code) => {
    // clog(id);
    try {
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/linkedin/access`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
      });
      if (!response.data.error) {
        if (response?.data?.isValid) {
          localStorage.setItem(
            "linkedin_access",
            JSON.stringify(response.data)
          );
        } else {
          localStorage.removeItem("linkedin_access");
        }
      } else {
        localStorage.removeItem("linkedin_access");
      }
    } catch (error) {
      clog(error);
      localStorage.removeItem("linkedin_access");
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
    } finally {
    }
  };
  async function checkGscAccess() {
    try {
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/google/search/website`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        params: {
          user_id: JSON.parse(localStorage.getItem("login_data"))?.resp_user
            ?.id,
        },
      });
      // clog(response, "response");
      if (!response.data.error) {
        if (response.data?.data?.access_token) {
          localStorage.setItem(
            "google_search_console_access",
            JSON.stringify(response.data?.data)
          );
          localStorage.setItem("gsc_website", response.data?.data?.profile_url);
          // setGsc_website(response.data?.data?.profile_url);
        } else {
          localStorage.removeItem("google_search_console_access");
          localStorage.removeItem("gsc_website");

          // setGsc_website(null);
        }
      } else {
        localStorage.removeItem("google_search_console_access");
        localStorage.removeItem("gsc_website");

        // setGsc_website(null);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      localStorage.removeItem("google_search_console_access");
      localStorage.removeItem("gsc_website");

      // setGsc_website(null);
    }
  }
  async function checkNotionAccess() {
    try {
      // setnotionLoading(true);
      let response = await axios({
        url: `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/notion/access`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          jwt_token: localStorage.getItem("login_token"),
        },
        params: {
          user_id: JSON.parse(localStorage.getItem("login_data"))?.resp_user
            ?.id,
        },
      });
      // clog(response, "response");
      if (!response.data.error) {
        if (response.data?.access_token) {
          localStorage.setItem("notion_access", JSON.stringify(true));
          // setnotion_access(true);
          // setNotionAccessList(response.data?.data);
        } else {
          localStorage.setItem("notion_access", JSON.stringify(false));
          // setnotion_access(false);
          // setNotionAccessList([]);
        }
      } else {
        localStorage.setItem("notion_access", JSON.stringify(false));
        // setnotion_access(false);
        // setNotionAccessList([]);
      }
    } catch (error) {
      clog(error);
      if (error.response && error?.response?.status == 401) {
        localStorage.removeItem("login_token");
        localStorage.removeItem("login_data");
        navigate("/");
      }
      localStorage.setItem("notion_access", JSON.stringify(false));
      // setnotion_access(false);
      // setNotionAccessList([]);
    }
  }
  useEffect(() => {
    // notify("Welcome to the Knowledge Base");
    getLinkedInAccessToken();
    checkGscAccess();
    checkNotionAccess();
  }, []);

  clog(selectedWorkspace);

  clog(path);
  const notify = (message, type) =>
    toast(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      type: type,
    });
  const getFormattedWorkspaceName = () => {
    const workspaceName = JSON.parse(
      localStorage.getItem("selected_workspace") || "{}"
    )?.workspace?.workspace_name?.trim();

    // Check if workspaceName is not null or undefined
    if (workspaceName) {
      const formattedWorkspaceName =
        workspaceName.charAt(0).toUpperCase() +
        workspaceName.slice(1).toLowerCase();
      // clog(formattedWorkspaceName);
      return `${formattedWorkspaceName}'s Workspace`;
    } else {
      return "";
    }
  };

  return (
    <>
      {!asideBarType() && (
        <aside className="flex flex-col justify-between " id="asideBar">
          <div>
            <div className="relative">
              <div className="dashboard-logo sticky top-0 z-50 bg-[#FAFBFC]">
                {/* <Link to="/"> */}
                <img
                  src={
                    window.location.origin + "/images/VisonWithOReplaced.png"
                  }
                  // className="h-12"
                  alt="logo"
                />
                {/* </Link> */}
                <button type="button" onClick={() => menuToggle()}>
                  <svg className="icon">
                    <use href="#icon_menu"></use>
                  </svg>
                </button>
              </div>

              {/* Kwin Onboarding status button until 6 steps are completed */}
              {kwinOnboardingStatus?.filter(Boolean).length < 6 && <div 
                onClick={() => setIsKwinOnboardingModalOpen(true)} 
                className="relative flex items-center justify-between mx-[1rem] my-2 p-3 bg-white border border-gray-200 rounded-xl cursor-pointer overflow-hidden group hover:border-[#5E78F4] transition-all duration-300 hover:shadow-md"
              >
                {/* Progress bar background - width can be updated dynamically */}
                <div 
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] opacity-10 transition-all duration-500 ease-in-out" 
                  style={{ width: `${Math.round(((kwinOnboardingStatus?.filter(Boolean).length || 0) / 6) * 100) || 0 }%` }}
                />
                
                {/* Button Content */}
                <div className="relative z-10 flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <svg className="w-[18px] h-[18px] text-[#5E78F4] group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <p className="text-sm font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">Onboarding Status</p>
                  </div>
                  <span className="text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#5E78F4] to-[#F14CE2]">
                    {kwinOnboardingStatus?.filter(Boolean).length || 0}/6
                  </span>
                </div>
              </div>}
              
              <ul>
                {
                  <li>
                    <div
                      className="nav-link nav-toggle"
                      onClick={(e) => {
                        moreLink(e, "more2");
                      }}
                    >
                      <span>
                        <svg className="icon">
                          <use href="#icon_pixel"></use>
                        </svg>
                      </span>
                      <strong>Kwin</strong>
                    </div>
                    <div className="navmore-link" id="more2">
                      <ul className="navmore-list">
                        <MenuWithUpgradeStamp
                          menuKey="script"
                          to="/script"
                          pathname={path?.pathname}
                          icon="#icon_script"
                          label="Pixel"
                          onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                        />
                        <MenuWithUpgradeStamp
                          menuKey="user_activity"
                          to="/user-activity"
                          pathname={path?.pathname}
                          icon="#icon_reporting"
                          label="User Activity"
                          onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                        />
                        <MenuWithUpgradeStamp
                          menuKey="person_level_id"
                          to="/person-level-id"
                          pathname={path?.pathname}
                          icon="#icon_person_level"
                          label="Person Level ID"
                          onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                          searchParams={path.search}
                        />
                        <MenuWithUpgradeStamp
                          menuKey="company_level_id"
                          to="/company-level-id"
                          pathname={path?.pathname}
                          icon="#icon_company_level"
                          label="Company Level ID"
                          onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                          searchParams={path.search}
                        />
                        {/* Intent topic */}
                        <MenuWithUpgradeStamp
                          menuKey="intent_topic"
                          to="/signals"
                          pathname={path?.pathname}
                          icon="#icon_tags"
                          label="Signals"
                          onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                        >
                          <span className="ms-2 bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm shrink-menu-hide">
                            New
                          </span>
                        </MenuWithUpgradeStamp>
                        {/* email outreach */}
                        <MenuWithUpgradeStamp
                          menuKey={"email_outreach"}
                          to="/email-outreach"
                          extraTo={"/email-campaigns"}
                          pathname={path?.pathname}
                          icon="#icon_email_outreach"
                          label="Email Outreach"
                          onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                          searchParams={path.search}
                        />
                      </ul>
                    </div>
                  </li>
                }
                {/* <button onClick={notify}>Notify !</button> */}
                <MenuWithUpgradeStamp
                  menuKey={"knowledge_base"}
                  to="/knowledge"
                  pathname={path?.pathname}
                  icon="#icon_book"
                  label="Knowledge Base"
                  onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                />

                {/* <MenuWithUpgradeStamp
                  menuKey={"monthly_calendar"}
                  to={"/monthly-calendar"}
                  pathname={path?.pathname}
                  icon="#icon_calendar2"
                  label="Monthly Calendar"
                  onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                /> */}
                {/* <li>
                  <NavLink
                    className={`nav-link ${
                      path?.pathname == "/tools" && "active"
                    }`}
                    to="/"
                  >
                    <span>
                      <svg className="icon">
                        <use href="#icon_tools"></use>
                      </svg>
                    </span>
                    <strong>Tools</strong>
                  </NavLink>
                </li> */}
                {/* <li>
                  <NavLink
                    className={`nav-link ${
                      (path?.pathname == "/ai-agent" ||
                        path?.pathname.includes("ai-agentname")) &&
                      "active"
                    }`}
                    to="/ai-agent"
                  >
                    <span>
                      <svg className="icon">
                        <use href="#icon_ai"></use>
                      </svg>
                    </span>
                    <strong>AI Employee</strong>
                  </NavLink>
                </li> */}
                {/* <MenuWithUpgradeStamp
                  menuKey={"ai_employee"}
                  to="/ai-agent"
                  extraTo={"/ai-agentname"}
                  pathname={path?.pathname}
                  icon="#icon_ai"
                  label="AI Employee"
                  onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                /> */}
                {isUserOwnerOfWorkspace() && (
                  <li>
                    <div
                      className="nav-link nav-toggle"
                      onClick={(e) => {
                        moreLink(e, "integrationMenu");
                      }}
                    >
                      <span>
                        <svg className="icon">
                          <use href="#icon_integration"></use>
                        </svg>
                      </span>
                      <strong>Integrations</strong>
                    </div>
                    <div className="navmore-link" id="integrationMenu">
                      <ul className="navmore-list">
                        {/* <MenuWithUpgradeStamp
                          menuKey={"marketing"}
                          to="/integration-marketing"
                          pathname={path?.pathname}
                          icon="#icon_marketing"
                          label="Marketing"
                          onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                        /> */}

                        <MenuWithUpgradeStamp
                          menuKey={"sales"}
                          to="/integration-sales"
                          pathname={path?.pathname}
                          icon="#icon_sales"
                          label="Sales"
                          onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                        />

                        <MenuWithUpgradeStamp
                          menuKey={"social"}
                          to="/integration-social"
                          pathname={path?.pathname}
                          icon="#icon_social_new"
                          label="Social"
                          onUpgradeClick={() => setIsUpgradeModalOpen(true)}
                        />
                      </ul>
                    </div>
                  </li>
                )}

                <li>
                  <div
                    className={`nav-link ""`}
                    onClick={() => {
                      getNotificationsUnreadUpdate();
                      // getNotifications("all");
                      setIsOpen(true);
                    }}
                  >
                    <span>
                      <svg className="icon">
                        <use href="#icon_notification"></use>
                      </svg>
                    </span>
                    <strong>
                      Notifications{" "}
                      <span>
                        {notifications?.filter(
                          (notification) => notification?.status == 0
                        )?.length > 0 && (
                          <span>{`(${
                            notifications?.filter(
                              (notification) => notification?.status == 0
                            )?.length
                          })`}</span>
                        )}
                      </span>
                    </strong>
                  </div>
                </li>
                {/* <li>
                  <NavLink
                    className={`nav-link ${
                      path?.pathname == "/chat" && "active"
                    }`}
                    to="/chat"
                  >
                    <span>
                      <svg className="icon">
                        <use href="#icon_chat"></use>
                      </svg>
                    </span>
                    <strong>Chat</strong>
                  </NavLink>
                </li> */}
              </ul>
            </div>
          </div>
          {localStorage.getItem("login_data") && (
            <>
              <div className="w-full aside-modelbtn flex flex-col gap-4 content-around active p-4 relative mt-5">
                {getPlanDetails(true) == 6 && (
                  <button
                    className="py-2 px-6 rounded-[20px] font-[600] text-[18px] bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white  hover:border-white hover:scale-105 hover:bg-gradient-to-tr "
                    onClick={() => {
                      setIsUpgradeModalOpen(true);
                    }}
                  >
                    Upgrade
                    <svg className="icon text-[22px] ms-2">
                      <use href="#icon_arrow"></use>
                    </svg>
                  </button>
                )}
                {/* Modern credit progress viewer with gradient theme */}
                {/* <div className="bg-gradient-to-r from-[#F8F9FF] to-[#FFF5FE] p-4 rounded-[15px] border border-[#E5E7EB]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-[500] text-[#374151]">
                      Credits Used
                    </span>
                    <span className="text-xs font-[600] text-transparent bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] bg-clip-text">
                      {creditsData.credit_limit - creditsData.bal_credits} /{" "}
                      {creditsData.credit_limit}
                    </span>
                  </div>
                  <div className="relative w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div
                      className="absolute left-0 top-0 h-2 bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] rounded-full transition-all duration-500 ease-in-out"
                      style={{
                        width: `${
                          creditsData.credit_limit > 0
                            ? (
                                ((creditsData.credit_limit -
                                  creditsData.bal_credits) /
                                  creditsData.credit_limit) *
                                100
                              ).toFixed(2)
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-[#6B7280]">
                      {creditsData.bal_credits} credits remaining
                    </span>
                    <span className="text-xs font-[500] text-[#10B981]">
                      {creditsData.credit_limit > 0
                        ? (
                            ((creditsData.credit_limit -
                              creditsData.bal_credits) /
                              creditsData.credit_limit) *
                            100
                          ).toFixed(2)
                        : 0}
                      % used
                    </span>
                  </div>
                </div> */}
                <button
                  type="button"
                  className="w-full flex flex-row items-center justify-between hover:text-[#1A71A7]"
                  onClick={handleButtonClick}
                >
                  <div className="flex flex-row items-center">
                    <Avatar
                      type="workspace"
                      data={JSON.parse(
                        localStorage.getItem("selected_workspace") || "{}"
                      )}
                      size="50px"
                      fontSize="20px"
                    />
                    <div>
                      <span className="mx-4 break-all">
                        {JSON.parse(
                          localStorage.getItem("selected_workspace") || "{}"
                        )?.workspace?.workspace_name?.toUpperCase() || ""}
                      </span>
                    </div>
                  </div>
                  <div className="">
                    <svg className="icon text-[12px]">
                      <use href="#icon_downarrow"></use>
                    </svg>
                  </div>
                </button>

                {/* {showLogout && (
                <button
                  onClick={handleLogout}
                  className="w-full absolute top-[-40px] right-0 bg-gray-500 text-white px-4 py-2 rounded-md shadow-lg "
                >
                  Logout
                </button>
              )} */}
              </div>
            </>
          )}
        </aside>
      )}
      {asideBarType() && (
        <aside className="flex flex-col justify-between" id="asideBar">
          <div className="h-full flex flex-col justify-between">
            <div className="relative  lh-[72%]">
              <div className="h-full">
                <div className="px-[30px] relative">
                  <div
                    className="flex items-center cursor-pointer font-semibold py-[25px] sticky top-0 z-50 bg-[#FAFBFC]"
                    onClick={() => navigate("/script")}
                  >
                    <button type="button" className="hover:text-[#1A71A7] me-3">
                      <svg className="icon text-[26px]">
                        <use href="#icon_backarrow"></use>
                      </svg>
                    </button>
                    Back
                  </div>
                  <div>
                    <h5 className="font-bold text-[22px] pb-[20px]">
                      Settings
                    </h5>
                  </div>
                  <div className="w-full aside-modelbtn active pb-4">
                    <button
                      type="button"
                      className="flex items-center hover:text-[#1A71A7]"
                    >
                      <Avatar
                        type="workspace"
                        data={JSON.parse(
                          localStorage.getItem("selected_workspace") || "{}"
                        )}
                        size="50px"
                        fontSize="20px"
                      />
                      <span className="ms-1 inline-block">
                        {getFormattedWorkspaceName()}
                      </span>
                    </button>
                  </div>
                </div>
                <ul
                  className="h-[80%] overflow-hidden overflow-y-auto"
                  // ref={asideRef}
                >
                  <li className="pb-[5px]">
                    <NavLink className="nav-link" to="/account-settings">
                      <span>
                        <svg className="icon">
                          <use href="#icon_user"></use>
                        </svg>
                      </span>
                      <strong>Account Settings</strong>
                    </NavLink>
                  </li>
                  <hr />
                  <li className="pb-[5px]">
                    <NavLink className="nav-link" to="/member">
                      <span>
                        <svg className="icon">
                          <use href="#icon_member-group"></use>
                        </svg>
                      </span>
                      <strong>Members</strong>
                    </NavLink>
                  </li>
                  <hr />
                  {/* <li className="pb-[5px] l">
                    <NavLink className="nav-link" to="/manage-space">
                      <span>
                        <svg className="icon">
                          <use href="#icon_space"></use>
                        </svg>
                      </span>
                      <strong>Spaces</strong>
                    </NavLink>
                  </li> */}
                  <hr />
                  {/* <li className="pb-[5px]">
                    <NavLink className="nav-link" to="/subscription">
                      <span>
                        <svg className="icon">
                          <use href="#icon_subscription"></use>
                        </svg>
                      </span>
                      <strong>Subscription</strong>
                    </NavLink>
                  </li> */}
                  {/* <hr /> */}
                  <li className="pb-[5px]">
                    <NavLink className="nav-link" to="/billing">
                      <span>
                        <svg className="icon">
                          <use href="#icon_bill"></use>
                        </svg>
                      </span>
                      <strong>Billing</strong>
                    </NavLink>
                  </li>
                  <hr />
                  <li className="pb-[5px]">
                    <NavLink className="nav-link" to="/feedback">
                      <span>
                        <svg className="icon">
                          <use href="#icon_feedback"></use>
                        </svg>
                      </span>
                      <strong>Feedback</strong>
                    </NavLink>
                  </li>
                  <hr />
                </ul>
              </div>
            </div>
            <div className="">
              <ul className="">
                <li
                  className="flex items-center hover:text-[#1A71A7] whitespace-pre-wrap"

                  // className="flexmpt-[10px] font-[500] text-[18px] pl-4 assign-name mb-[-5px]"
                >
                  <Avatar
                    type="user"
                    data={
                      JSON.parse(localStorage.getItem("login_data"))?.resp_user
                    }
                    size="40px"
                    fontSize="16px"
                  />
                  <span className="ms-1">
                    {" "}
                    {JSON.parse(localStorage.getItem("login_data"))?.resp_user
                      ?.first_name +
                      " " +
                      JSON.parse(localStorage.getItem("login_data"))?.resp_user
                        ?.last_name}
                  </span>
                </li>
                <li className="">
                  <NavLink className="nav-link" to="/my-settings">
                    <span>
                      <svg className="icon">
                        <use href="#icon_setting"></use>
                      </svg>
                    </span>
                    <strong>My Settings</strong>
                  </NavLink>
                </li>
                <li className="">
                  <NavLink
                    className="nav-link"
                    to="/my-workspace"
                    onClick={() => {
                      setShowLogout(false);
                    }}
                  >
                    <span>
                      <svg className="icon">
                        <use href="#icon_workspace"></use>
                      </svg>
                    </span>
                    <strong>Workspaces</strong>
                  </NavLink>
                </li>
                {/* <li className="">
                  <NavLink className="nav-link" to="/">
                    <span>
                      <svg className="icon">
                        <use href="#icon_notification"></use>
                      </svg>
                    </span>
                    <strong>Notifications</strong>
                  </NavLink>
                </li> */}
                <li
                  className=""
                  onClick={() => {
                    logoutApi();
                    // navigate("/");
                  }}
                >
                  <NavLink className="nav-link" to="/">
                    <span>
                      <svg className="icon">
                        <use href="#icon_logout"></use>
                      </svg>
                    </span>
                    <strong>Logout</strong>
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>
        </aside>
      )}

      {/* offcanvas */}
      {/* <div className="flex min-h-[50vh] items-center justify-center">
        <Button onClick={() => setIsOpen(true)}>Show right drawer</Button>
      </div> */}
      <Drawer
        open={isOpen}
        onClose={handleClose}
        position="right"
        className="p-0 w-[580px] notification-canvas"
      >
        <Drawer.Header title="Notification" className="pt-3" />
        <Drawer.Items className="">
          <div className="">
            {/* <div className="px-[20px] border-y border-[#C9C9C9] bg-[#F9F9F9] h-[90px] flex items-center relative">
              <div className="w-[50px] h-[50px] bg-[#F3F3F3] flex items-center justify-center rounded-full">
                <svg className="icon text-[22px]">
                  <use href="#icon_book"></use>
                </svg>
              </div>
              <div className="ms-3">
                <h6 className="text[18px] font-[400]">
                  Your <span className="font-[600]">Knowledge Based</span> Has
                  Been <span className="font-[600]">Created</span>
                </h6>
                <p className="text-[#A0A0A0] text-[16px] font-[400]">
                  10 Mins. ago
                </p>
              </div>
              <div className="w-[10px] h-[10px] bg-[#097BA9] rounded-full absolute right-[20px] top-[20px]"></div>
            </div> */}
            {notificationsLoading ? (
              // Skeleton-wave loader while notifications are being fetched
              <div className="px-[20px] py-4 space-y-4">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center relative border-b border-[#E9E9E9] pb-3"
                  >
                    <div className="w-[50px] min-w-[50px] h-[50px] min-h-[50px] rounded-full bg-gray-300 skeleton-wave"></div>
                    <div className="ms-3 flex-1">
                      <div className="h-4 w-48 bg-gray-300 rounded skeleton-wave mb-2"></div>
                      <div className="h-3 w-24 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                    <div className="w-[10px] h-[10px] rounded-full bg-gray-300 skeleton-wave absolute right-[20px] top-[20px]"></div>
                  </div>
                ))}
              </div>
            ) : notifications?.length > 0 ? (
              <Accordion>
                {getDatesWiseNotifications()?.map((notification, index) => (
                  <Accordion.Panel key={index}>
                    <Accordion.Title className="bg-[#F9F9F9]">
                      <div className="month  px-[20px]  text-[14px] font-[400] flex items-center text-[#A8A8A8]">
                        {/* {moment(notification?.createdAt)?.format("MMM YYYY")} */}
                        {notification?.monthYear}
                      </div>
                    </Accordion.Title>
                    <Accordion.Content>
                      {notification?.notifications
                        ?.reverse()
                        ?.map((notification, index, notification_array) => (
                          <>
                            <div
                              className={
                                notification?.status === 0
                                  ? "px-[20px] border-y border-[#C9C9C9] bg-[#F9F9F9]  flex items-center relative"
                                  : "px-[20px]  flex items-center relative my-2"
                              }
                            >
                              <div className="w-[50px] min-w-[50px] h-[50px] min-h-[50px] bg-[#F3F3F3] flex items-center justify-center rounded-full">
                                <svg className="icon text-[22px]">
                                  <use href="#icon_book"></use>
                                </svg>
                              </div>
                              <div className="ms-3">
                                <h6 className="text[18px] font-[400]">
                                  {notification?.contents}
                                </h6>
                                <p className="text-[#A0A0A0] text-[16px] font-[400]">
                                  {getCreatedAt(notification?.createdAt)}
                                </p>
                                {notification?.status == 0 && (
                                  <div className="w-[10px] h-[10px] bg-[#097BA9] rounded-full absolute right-[20px] top-[20px]"></div>
                                )}
                              </div>
                            </div>
                            {notification_array.length - 1 != index && <hr />}
                          </>
                        ))}
                    </Accordion.Content>
                  </Accordion.Panel>
                ))}
              </Accordion>
            ) : (
              <div className="px-[20px] py-6 text-center text-[#A0A0A0] text-[14px]">
                No notifications yet
              </div>
            )}
            {/* <Accordion>
              <Accordion.Panel>
                <Accordion.Title className="bg-[#F9F9F9]">
                  <div className="month  px-[20px]  text-[14px] font-[400] flex items-center text-[#A8A8A8] ">
                    Sep 2024
                  </div>
                </Accordion.Title>
                <Accordion.Content>
                  <div className="px-[20px] h-[90px] flex items-center relative">
                    <div className="w-[50px] h-[50px] bg-[#F3F3F3] flex items-center justify-center rounded-full">
                      <svg className="icon text-[22px]">
                        <use href="#icon_book"></use>
                      </svg>
                    </div>
                    <div className="ms-3">
                      <h6 className="text[18px] font-[400]">
                        Your <span className="font-[600]">Knowledge Based</span>{" "}
                        Has Been <span className="font-[600]">Created</span>
                      </h6>
                      <p className="text-[#A0A0A0] text-[16px] font-[400]">
                        Sep 20.
                      </p>
                    </div>
                  </div>
                  <hr />
                  <div className="px-[20px] h-[90px] flex items-center relative">
                    <div className="w-[50px] h-[50px] bg-[#F3F3F3] flex items-center justify-center rounded-full">
                      <svg className="icon text-[22px]">
                        <use href="#icon_ai"></use>
                      </svg>
                    </div>
                    <div className="ms-3">
                      <h6 className="text[18px] font-[400]">
                        Your <span className="font-[600]">AI Employee</span>{" "}
                        Output Is Ready
                      </h6>
                      <p className="text-[#A0A0A0] text-[16px] font-[400]">
                        Sep 15.
                      </p>
                    </div>
                  </div>
                  <hr />
                  <div className="px-[20px] h-[90px] flex items-center relative">
                    <div className="w-[50px] h-[50px] bg-[#F3F3F3] flex items-center justify-center rounded-full">
                      <svg className="icon text-[22px]">
                        <use href="#icon_ai"></use>
                      </svg>
                    </div>
                    <div className="ms-3">
                      <h6 className="text[18px] font-[400]">
                        Your <span className="font-[600]">AI Employee</span>{" "}
                        Output Is Ready
                      </h6>
                      <p className="text-[#A0A0A0] text-[16px] font-[400]">
                        Sep 13.
                      </p>
                    </div>
                  </div>
                </Accordion.Content>
              </Accordion.Panel>
              <Accordion.Panel>
                <Accordion.Title className="bg-[#F9F9F9]">
                  <div className="month  px-[20px]  text-[14px] font-[400] flex items-center text-[#A8A8A8]">
                    Aug 2024
                  </div>
                </Accordion.Title>
                <Accordion.Content>
                  <div className="px-[20px] h-[90px] flex items-center relative">
                    <div className="w-[50px] h-[50px] bg-[#F3F3F3] flex items-center justify-center rounded-full">
                      <svg className="icon text-[22px]">
                        <use href="#icon_book"></use>
                      </svg>
                    </div>
                    <div className="ms-3">
                      <h6 className="text[18px] font-[400]">
                        Your <span className="font-[600]">Knowledge Based</span>{" "}
                        Has Been <span className="font-[600]">Created</span>
                      </h6>
                      <p className="text-[#A0A0A0] text-[16px] font-[400]">
                        Aug 20.
                      </p>
                    </div>
                  </div>
                  <hr />
                  <div className="px-[20px] h-[90px] flex items-center relative">
                    <div className="w-[50px] h-[50px] bg-[#F3F3F3] flex items-center justify-center rounded-full">
                      <svg className="icon text-[22px]">
                        <use href="#icon_ai"></use>
                      </svg>
                    </div>
                    <div className="ms-3">
                      <h6 className="text[18px] font-[400]">
                        Your <span className="font-[600]">AI Employee</span>{" "}
                        Output Is Ready
                      </h6>
                      <p className="text-[#A0A0A0] text-[16px] font-[400]">
                        Aug 15.
                      </p>
                    </div>
                  </div>
                  <hr />
                  <div className="px-[20px] h-[90px] flex items-center relative">
                    <div className="w-[50px] h-[50px] bg-[#F3F3F3] flex items-center justify-center rounded-full">
                      <svg className="icon text-[22px]">
                        <use href="#icon_ai"></use>
                      </svg>
                    </div>
                    <div className="ms-3">
                      <h6 className="text[18px] font-[400]">
                        Your <span className="font-[600]">AI Employee</span>{" "}
                        Output Is Ready
                      </h6>
                      <p className="text-[#A0A0A0] text-[16px] font-[400]">
                        Aug 13.
                      </p>
                    </div>
                  </div>
                </Accordion.Content>
              </Accordion.Panel>
            </Accordion> */}
          </div>
        </Drawer.Items>
      </Drawer>
      <ToastContainer />
      <div
        style={{ display: showLogout ? "block" : "none" }}
        ref={modalRef}
        className="custom-model  w-[420px] bg-[#fff] fixed left-[15px] bottom-[20px] z-50 rounded-[15px] overflow-hidden shadow-lg shadow-[#00000040]"
      >
        <div className="flex items-center w-full p-4 bg-gradient-to-r from-[#097BA9] to-[#9B3785]">
          <div>
            <img
              src={window.location.origin + "/images/white-logo.png"}
              alt="logo"
            />
          </div>
          <div className="mx-auto">
            {/* {(getPlanDetails() == 1 || getPlanDetails() == 2) && ( */}
            <button
              type="button"
              className="py-1  font-semibold px-4 bg-[#fff] rounded-[20px] mix-blend-screen flex items-center"
              onClick={() => navigate("/subscription")}
              style={{
                display: "none",
              }}
            >
              Upgrade
              <svg className="icon text-[22px] ms-2">
                <use href="#icon_arrow"></use>
              </svg>
            </button>
            {/* )} */}
          </div>
          <div>
            <button
              type="button"
              className="text-[#fff] hover:text-[red]"
              onClick={() => setShowLogout(false)}
            >
              <svg className="icon text-[32px]">
                <use href="#icon_close"></use>
              </svg>
            </button>
          </div>
        </div>
        <div className="p-4 w-full flex">
          <div className="w-[18%] px-2 flex flex-col">
            {/* <Tooltip
              content="Add Workspace"
              placement="right"
              className="tooltip"
            >
              <button
                type="button"
                className="mb-3"
                onClick={() => {
                  if (!checkWorkspaceEnabled()) {
                    setpopupMessage("Add New Workspace");
                    setshowModal({
                      first: false,
                      second: true,
                      third: false,
                    });
                    return;
                  } else {
                    navigate("/add-workspace");
                  }
                }}
              >
                <div className="w-[40px] h-[40px] bg-[#7BFF78] text-[#fff] font-semibold text-[16px] rounded-full flex items-center justify-center hover:bg-[#097BA9]">
                  <svg className="icon text-[18px]">
                    <use href="#icon_plus"></use>
                  </svg>
                </div>
              </button>
            </Tooltip> */}
            <div className="max-h-[160px] overflow-auto">
              {workspaceList?.map((workspace, workspace_index) => (
                <Tooltip
                  content={workspace?.workspace?.workspace_name}
                  placement="right"
                  className="tooltip"
                  key={workspace_index}
                >
                  <button
                    type="button"
                    onClick={() => {
                      switchWorkspace(workspace);
                    }}
                  >
                    <Avatar
                      type="workspace"
                      data={workspace}
                      size="40px"
                      fontSize="16px"
                      className="mb-3"
                    />
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
          <div className="w-[51%]  border-r px-[12px]">
            <button
              type="button"
              className="flex items-center hover:text-[#1A71A7] whitespace-pre-wrap"
            >
              <Avatar
                type="workspace"
                data={JSON.parse(
                  localStorage.getItem("selected_workspace") || "{}"
                )}
                size="40px"
                fontSize="16px"
              />
              <span className="ms-1 break-all">
                {JSON.parse(
                  localStorage.getItem("selected_workspace") || "{}"
                )?.workspace?.workspace_name?.toUpperCase()}
                <div>Workspace</div>
              </span>
            </button>
            <ul className="pt-2">
              <li className="pb-2">
                <button
                  type="button"
                  className="text-[#B3B3B3] hover:text-[#097BA9] flex items-center"
                  onClick={() => {
                    setShowLogout(false);
                    navigate("/account-settings");
                  }}
                >
                  <svg className="icon me-2 text-[22px]">
                    <use href="#icon_setting"></use>
                  </svg>
                  Settings
                </button>
              </li>
              <li className="pb-2">
                <button
                  type="button"
                  className="text-[#B3B3B3] hover:text-[#097BA9] flex items-center"
                  onClick={() => {
                    setShowLogout(false);
                    navigate("/member");
                  }}
                >
                  <svg className="icon me-2 text-[22px]">
                    <use href="#icon_member"></use>
                  </svg>
                  Members
                </button>
              </li>
              {/* <li className="pb-2 ">
                <button
                  type="button"
                  className="text-[#B3B3B3] hover:text-[#097BA9] flex items-center"
                  onClick={() => {
                    setShowLogout(false);
                    navigate("/manage-space");
                  }}
                >
                  <svg className="icon me-2 text-[22px]">
                    <use href="#icon_space"></use>
                  </svg>
                  Spaces
                </button>
              </li> */}
              {/* <li className="pb-2">
                <button
                  type="button"
                  className="text-[#B3B3B3] hover:text-[#097BA9] flex items-center"
                >
                  <svg className="icon me-2 text-[22px]">
                    <use href="#icon_folder"></use>
                  </svg>
                  Folders
                </button>
              </li> */}
              <li>
                <button
                  type="button"
                  className="text-[#B3B3B3] hover:text-[#097BA9] flex items-center"
                  onClick={() => {
                    setShowLogout(false);
                    navigate("/billing");
                  }}
                >
                  <svg className="icon me-2 text-[22px]">
                    <use href="#icon_bill"></use>
                  </svg>
                  Billings
                </button>
              </li>
            </ul>
          </div>
          <div className="w-[41%] px-[12px]">
            <button
              type="button"
              className="flex items-center hover:text-[#1A71A7] whitespace-pre-wrap"
            >
              <Avatar
                type="user"
                data={JSON.parse(localStorage.getItem("login_data"))?.resp_user}
                size="40px"
                fontSize="16px"
              />
              <span className="ms-1">
                {" "}
                {JSON.parse(localStorage.getItem("login_data"))?.resp_user
                  ?.first_name +
                  " " +
                  JSON.parse(localStorage.getItem("login_data"))?.resp_user
                    ?.last_name}
              </span>
            </button>
            <ul className="pt-2">
              <li className="pb-2">
                <button
                  type="button"
                  className="text-[#B3B3B3] hover:text-[#097BA9] flex items-center"
                  onClick={() => {
                    setShowLogout(false);
                    navigate("/my-settings");
                  }}
                >
                  <svg className="icon me-2 text-[22px]">
                    <use href="#icon_setting"></use>
                  </svg>
                  My Settings
                </button>
              </li>
              {/* <li className="pb-2">
                <button
                  type="button"
                  className="text-[#B3B3B3] hover:text-[#097BA9] flex items-center"
                >
                  <svg className="icon me-2 text-[22px]">
                    <use href="#icon_bell"></use>
                  </svg>
                  Notification
                </button>
              </li> */}
              <li className="pb-2">
                <button
                  type="button"
                  className="text-[#B3B3B3] hover:text-[#097BA9] flex items-center"
                  onClick={() => {
                    logoutApi();
                    // navigate("/");
                  }}
                >
                  <svg className="icon me-2 text-[22px]">
                    <use href="#icon_logout"></use>
                  </svg>
                  Log Out
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        setIsOpen={setIsUpgradeModalOpen}
      />
      <KwinOnboardingModal
          kwinOnboardingStatus={kwinOnboardingStatus}
          setKwinOnboardingStatus={setKwinOnboardingStatus}
          isOpen={isKwinOnboardingModalOpen}      
          setIsOpen={setIsKwinOnboardingModalOpen}
      />
      <PopupModals
        showModal={showModal}
        setshowModal={setshowModal}
        popupMessage={popupMessage}
      />
    </>
  );
}
export default AsideBar;
